import {
  CoachProfile,
  DoterSpecies,
  LoggedInParent,
  UserRole,
} from "@joonapp/web-shared"

import {
  PlannedEvent,
  PlannedEventIdentifier,
  ReferralGiftCard,
  TherapistProfile,
} from "../../types"
import { AddChildRequest } from "../../types"
import { createJoonAPIClient } from "../../util/joon-api"

export async function loadUser() {
  const joonApiClient = createJoonAPIClient()
  try {
    const response = await joonApiClient.get<LoggedInParent>("api/users/me", {
      params: { update_last_login: true },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getSBCredentials = async (userId: number) => {
  const API = createJoonAPIClient()
  return API.get<{ sendbird_user_id: string; sendbird_access_token: string }>(
    `api/users/${userId}/sendbird-access-token/`,
    {
      params: { role: UserRole.CLIENT },
    }
  ).then((response) => response.data)
}

export async function loadCareTeam(userId: number) {
  const API = createJoonAPIClient()
  try {
    const response = await API.get<{
      profiles: (CoachProfile | TherapistProfile)[]
    }>(`api/users/${userId}/care-team/`)
    return response.data
  } catch (error) {
    throw error
  }
}

export async function editChild({
  childInfo,
  childId,
}: {
  childInfo: AddChildRequest
  childId: number
}) {
  const API = createJoonAPIClient()
  return API.patch(`api/users/${childId}/`, childInfo).then((res) => res.data)
}

export async function editParent({
  parentInfo,
  userId,
  parentDoter,
}: {
  parentInfo: {
    name?: string
    nickname?: string
    last_name?: string
    phone_number?: string // (format: "+12533914941")
    birthdate?: string // (format: "YYYY-MM-DD")
  }
  parentDoter?: {
    id?: number
    type: DoterSpecies
  }
  userId: number
}) {
  if (!userId) throw new Error("No user id")
  const API = createJoonAPIClient()
  if (parentDoter) {
    await API.patch(`api/parent-doters/${parentDoter.id}/`, {
      type: parentDoter.type,
    })
  }
  return API.patch(`api/users/${userId}/`, parentInfo).then((res) => res.data)
}

export async function editPasscode({
  // used for both parent and child passcodes
  userId,
  accountPasscode,
}: {
  userId: number
  accountPasscode?: string | null // null deletes the passcode
}) {
  if (!userId) throw new Error("No user id")
  const API = createJoonAPIClient()
  return API.patch(`api/users/${userId}/account-passcode/`, {
    passcode: accountPasscode,
  }).then((res) => res.data)
}

export async function editParentPanelPasscode({
  userId,
  parentPanelPasscode,
}: {
  userId: number
  parentPanelPasscode: string | null // null deletes the passcode
}) {
  if (!userId) throw new Error("No user id")
  const API = createJoonAPIClient()
  return API.patch(`api/users/${userId}/parent-panel-passcode/`, {
    passcode: parentPanelPasscode,
  }).then((res) => res.data)
}

export async function addUpdateUserAddress({
  addressInfo,
  userId,
}: {
  addressInfo: {
    address_line_1?: string
    address_line_2?: string | null
    city?: string
    state?: string
    postal?: string
    country?: string
  }
  userId: number
}) {
  if (!userId) throw new Error("No user id")
  const API = createJoonAPIClient()
  return API.post(`api/users/${userId}/address/`, addressInfo).then(
    (res) => res.data
  )
}

export async function getReferralInfo(uuid: string) {
  const API = createJoonAPIClient()
  return API.post<ReferralGiftCard | null>(
    `api/referral-gift-cards/validate/`,
    { uuid }
  ).then((res) => res.data)
}

export async function createReferralGiftCard() {
  const API = createJoonAPIClient()
  return API.post<ReferralGiftCard | null>(`api/referral-gift-cards/`).then(
    (res) => res.data
  )
}

export async function submitReferralCode(referralCode: string) {
  const API = createJoonAPIClient()
  return API.post<LoggedInParent | null>(`api/referral-gift-cards/apply/`, {
    uuid: referralCode,
  })
}

// Returns a list of planned events. If you have the identifier set, then results should be an array of 1
export async function getPlannedEvents(identifier: PlannedEventIdentifier) {
  const API = createJoonAPIClient()
  return API.get<{ results: PlannedEvent[] }>(`api/planned-events/`, {
    params: { identifier },
  }).then((res) => res.data.results)
}
